import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Mail, Phone, MapPin, Building2, Filter } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "closed"];
const STATUS_STYLES = {
  new: "bg-orange-100 text-orange-800",
  contacted: "bg-blue-100 text-blue-800",
  closed: "bg-stone-200 text-stone-700",
};

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    const params = filter ? { status_filter: filter } : {};
    api.get("/admin/enquiries", { params })
      .then((r) => setEnquiries(r.data))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setBusy(false));
  };

  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Contractor Enquiries</h1>
      <p className="mt-1 text-base font-medium text-sarthi-muted">Leads from the About / Contact form.</p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-sarthi-muted" />
        <button data-testid="enq-filter-all" onClick={() => setFilter("")} className={`rounded-full border-2 px-4 py-2 text-sm font-bold ${!filter ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}>
          All{!filter && ` (${enquiries.length})`}
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button key={s} data-testid={`enq-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full border-2 px-4 py-2 text-sm font-bold capitalize ${filter === s ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}>
            {s}
          </button>
        ))}
      </div>

      {busy && enquiries.length === 0 ? (
        <div className="mt-10 text-center text-sarthi-muted">Loading…</div>
      ) : enquiries.length === 0 ? (
        <div className="sarthi-card mt-6 p-8 text-center text-sarthi-muted">No enquiries yet.</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {enquiries.map((e) => (
            <div key={e.id} data-testid={`enq-row-${e.id}`} className="sarthi-card p-5">
              <div className="mb-2 flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-extrabold text-sarthi-text">{e.name}</div>
                  <div className="inline-flex items-center gap-1 text-sm font-bold text-sarthi-secondary"><Building2 className="h-4 w-4"/>{e.company}</div>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${STATUS_STYLES[e.status] || ""}`}>{e.status}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold text-sarthi-muted">
                <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-sarthi-primary"><Mail className="h-4 w-4"/>{e.email}</a>
                <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-sarthi-primary"><Phone className="h-4 w-4"/>{e.phone}</a>
                {e.location && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4"/>{e.location}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-md bg-sarthi-accent px-2 py-1 text-xs font-bold text-sarthi-primary">{e.project_type}</span>
                {e.headcount && <span className="rounded-md bg-sarthi-accent px-2 py-1 text-xs font-bold text-sarthi-primary">{e.headcount} people</span>}
                {e.start_date && <span className="rounded-md bg-sarthi-accent px-2 py-1 text-xs font-bold text-sarthi-primary">Start {e.start_date}</span>}
              </div>
              {(e.manpower_categories || []).length > 0 && (
                <div className="mt-2 text-sm font-semibold capitalize text-sarthi-muted">
                  Needs: {(e.manpower_categories || []).join(", ")}
                </div>
              )}
              {e.message && <div className="mt-3 rounded-lg bg-sarthi-accent/60 p-3 text-sm">{e.message}</div>}
              <div className="mt-4 flex flex-wrap gap-2">
                {STATUS_OPTIONS.filter((s) => s !== e.status).map((s) => (
                  <button
                    key={s}
                    data-testid={`enq-set-${e.id}-${s}`}
                    onClick={() => updateStatus(e.id, s)}
                    className="rounded-lg border-2 border-sarthi-border bg-white px-3 py-1.5 text-xs font-bold uppercase hover:border-sarthi-primary hover:text-sarthi-primary"
                  >
                    Mark {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
